import type { getTranslations } from 'next-intl/server'

/**
 * Brew University: the founders' product walkthroughs on YouTube,
 * grouped the way someone learns Brew. Chapters and
 * their start times come from the transcribed video library (each start sits
 * a few seconds before its topic, as the library sets it); upload dates and
 * lengths are YouTube's. `/university`, its Markdown twin and the videos
 * shown beside related pages all read this file; their words (titles,
 * descriptions, chapter names) are the `university` catalog's, in the
 * reader's language. The videos themselves are English.
 */

export const UNIVERSITY_PATH = '/university'

export const UNIVERSITY_CHANNEL_URL =
  'https://www.youtube.com/@brewdotnew/videos'

type UniversityVideoData = Readonly<{
  /** The YouTube video ID. */
  id: string
  /** Who walks through it, as the video and its thumbnail name them. */
  presenter: 'Philip' | 'Thomas'
  seconds: number
  uploadDate: string
  /** Each chapter's start, in seconds; its name is `videos.<key>.chapters.s<start>`. */
  chapters: ReadonlyArray<number>
}>

const UNIVERSITY_VIDEO_DATA = {
  createEditSend: {
    id: 'eZyx59lzEz8',
    presenter: 'Philip',
    seconds: 374,
    uploadDate: '2026-08-03T13:38:44-07:00',
    chapters: [12, 87, 115, 175, 250, 324],
  },
  brand: {
    id: 'PPGG6XkfcvE',
    presenter: 'Thomas',
    seconds: 266,
    uploadDate: '2026-08-03T13:39:47-07:00',
    chapters: [6, 36, 85, 166, 198, 238],
  },
  consistentDesign: {
    id: '8bO76hfPuog',
    presenter: 'Philip',
    seconds: 506,
    uploadDate: '2026-08-03T13:38:56-07:00',
    chapters: [36, 136, 192, 249, 301, 337, 426],
  },
  importEmails: {
    id: 'm1QowAm1IDA',
    presenter: 'Thomas',
    seconds: 215,
    uploadDate: '2026-08-03T13:38:47-07:00',
    chapters: [12, 51, 92, 165],
  },
  rendering: {
    id: 'JZEyZaZ6aCg',
    presenter: 'Philip',
    seconds: 272,
    uploadDate: '2026-08-03T13:39:11-07:00',
    chapters: [7, 102, 158, 191, 219],
  },
  contacts: {
    id: 'HxfYKJbE4WM',
    presenter: 'Thomas',
    seconds: 300,
    uploadDate: '2026-08-03T13:39:08-07:00',
    chapters: [19, 45, 82, 174, 218],
  },
  automations: {
    id: 'F2ReiamTeM8',
    presenter: 'Philip',
    seconds: 440,
    uploadDate: '2026-08-03T13:38:50-07:00',
    chapters: [55, 108, 158, 252, 287, 359],
  },
  claude: {
    id: 'W8ZATx3v_YY',
    presenter: 'Thomas',
    seconds: 461,
    uploadDate: '2026-08-03T13:38:53-07:00',
    chapters: [37, 63, 133, 237, 270],
  },
  grokBot: {
    id: 'cQRBsrwNKyA',
    presenter: 'Philip',
    seconds: 58,
    uploadDate: '2026-08-16T04:14:24-07:00',
    chapters: [],
  },
} as const satisfies Record<string, UniversityVideoData>

export type UniversityVideoKey = keyof typeof UNIVERSITY_VIDEO_DATA

export const UNIVERSITY_VIDEO_KEYS = Object.keys(
  UNIVERSITY_VIDEO_DATA
) as ReadonlyArray<UniversityVideoKey>

/**
 * The page's sections, in the order someone learns Brew. The first one sits
 * right under the hero with no heading of its own.
 */
export const UNIVERSITY_GROUPS = [
  { id: 'start', titled: false, videos: ['brand', 'createEditSend'] },
  {
    id: 'design',
    titled: true,
    videos: ['consistentDesign', 'importEmails', 'rendering'],
  },
  { id: 'audiences', titled: true, videos: ['contacts', 'automations'] },
  { id: 'agents', titled: true, videos: ['claude', 'grokBot'] },
] as const satisfies ReadonlyArray<
  Readonly<{
    id: string
    titled: boolean
    videos: ReadonlyArray<UniversityVideoKey>
  }>
>

export type UniversityGroupId = (typeof UNIVERSITY_GROUPS)[number]['id']

type UniversityChapter = Readonly<{ title: string; t: number }>

/** A video as a page shows it: its data and its words, in the reader's language. */
export type UniversityVideo = Readonly<{
  key: UniversityVideoKey
  id: string
  title: string
  description: string
  presenter: 'Philip' | 'Thomas'
  seconds: number
  uploadDate: string
  chapters: ReadonlyArray<UniversityChapter>
}>

export type UniversityGroup = Readonly<{
  id: UniversityGroupId
  /** Absent for the untitled first section. */
  title?: string
  description?: string
  videos: ReadonlyArray<UniversityVideo>
}>

/** `getTranslations('university')`. */
export type UniversityTranslator = Awaited<
  ReturnType<typeof getTranslations<'university'>>
>

type UniversityMessageKey = Parameters<UniversityTranslator>[0]

export function getUniversityVideo(
  t: UniversityTranslator,
  key: UniversityVideoKey
): UniversityVideo {
  const data: UniversityVideoData = UNIVERSITY_VIDEO_DATA[key]
  return {
    key,
    id: data.id,
    title: t(`videos.${key}.title`),
    description: t(`videos.${key}.description`),
    presenter: data.presenter,
    seconds: data.seconds,
    uploadDate: data.uploadDate,
    chapters: data.chapters.map((start) => ({
      t: start,
      title: t(`videos.${key}.chapters.s${start}` as UniversityMessageKey),
    })),
  }
}

/** The sections, each with its videos, in the reader's language. */
export function getUniversityGroups(
  t: UniversityTranslator
): ReadonlyArray<UniversityGroup> {
  return UNIVERSITY_GROUPS.map((group) => ({
    id: group.id,
    ...(group.titled
      ? {
          title: t(`groups.${group.id}.title`),
          description: t(`groups.${group.id}.description`),
        }
      : {}),
    videos: group.videos.map((key) => getUniversityVideo(t, key)),
  }))
}

/** "7:41". */
export function formatVideoLength(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/** "PT7M41S", for VideoObject structured data. */
export function isoVideoDuration(seconds: number): string {
  return `PT${Math.floor(seconds / 60)}M${seconds % 60}S`
}

/** A link that opens the video at `t` seconds on YouTube. */
export function videoUrlAt(video: Pick<UniversityVideo, 'id'>, t = 0): string {
  return t > 0
    ? `https://youtu.be/${video.id}?t=${t}`
    : `https://youtu.be/${video.id}`
}

/** The words a `VideoCard` shows around a video, in the reader's language. */
export type VideoCardLabels = Readonly<{
  play: string
  byline: string
  watchOnYouTube: string
  opensInNewTab: string
}>

export function getVideoCardLabels(
  t: UniversityTranslator,
  video: UniversityVideo
): VideoCardLabels {
  const length = formatVideoLength(video.seconds)
  return {
    play: t('card.play', { title: video.title, length }),
    byline: t('card.byline', {
      presenter: t(`presenters.${video.presenter}`),
      length,
    }),
    watchOnYouTube: t('card.watchOnYouTube'),
    opensInNewTab: t('card.opensInNewTab'),
  }
}
